import { axiosReq, axiosInstance } from "../../../utils/axios";

/* Store */
import imagesReducer, { setImagesUploading } from "../store/images";

/* Static */
import { devEnterpriseUploadImageGetUrl } from "../static/devdata";


/**
 * Class to represent a selected image with both File and Blob references.
 */
export class LocalImageWithCropBlob {
    /**
     * @param {File} file - The original local file selected by the user.
     * @param {Blob} blob - The processed/cropped Blob version of the image.
     */
    constructor(file, blob) {
        this.file = file;
        this.blob = blob;
    }
}

const toBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            resolve(reader.result);
        };
        reader.onerror = (error) => reject(error);
    });

export const tryPostImage = (file) => {
    return async (dispatch) => {
        dispatch(setImagesUploading(true));
        let base64 = file;
        if (file instanceof File) {
            base64 = await toBase64(file);
        }
        const image = await axiosReq("apiv1/coach/images/upload", "POST", { image: base64 }, devEnterpriseUploadImageGetUrl, { "x-force-preflight": "true" });
        dispatch(setImagesUploading(false));
        if (image.hasOwnProperty("error")) {
            return image.error;
        } else {
            return image.image;
        }
    };
};

export const tryPostImageNoDispatch = async (file) => {
    let formData = new FormData();
    if (typeof file === "string" && file.startsWith("data:")) {
        const fileBlob = await fetch(file).then((res) => res.blob());
        formData.append("file", fileBlob, "image");
    } else if (file instanceof File) {
        formData.append("file", file, file.name || "image");
    } else if (file instanceof Blob) {
        formData.append("file", file, "image");
    } else if (file instanceof LocalImageWithCropBlob) {
        formData.append("file", file.blob, file.file.name || "image");
    } else {
        console.error("Invalid file type:", Object.prototype.toString.call(file));
        console.error(file)
        throw new Error(`Invalid file type. Received type: ${Object.prototype.toString.call(file)}. Expected a File, Blob, base64 data URL string, or image URL.`);
    }
    const responseData = await axiosReq("api/v2/upload/images", "POST_FORMDATA", formData, devEnterpriseUploadImageGetUrl, { "x-force-preflight": "true" });
    if (responseData.hasOwnProperty("error")) {
        throw new Error(responseData.error);
    } else {
        return responseData.images[0].imageUrl;
    }
};

/**
 * Uploads an image file to the server using a POST request to "/api/v2/upload/images".
 * Supports File, Blob, base64 data URL string, or image URL as input.
 *
 * @async
 * @function
 * @param {File|Blob|string} file - The image to upload. Can be a File, Blob, or a base64 data URL string.
 * @param {function(number, ProgressEvent):void} [onProgress] - Optional callback for upload progress. Receives percent completed and the ProgressEvent.
 * @returns {Promise<string>} Resolves with the uploaded image URL.
 * @throws {Error} Throws if the file type is invalid or if the server returns an error.
 */
export const tryPostImageNoDispatchV2 = async (file, onProgress) => {
    let formData = new FormData();
    if (file instanceof LocalImageWithCropBlob) {
        formData.append("file", file.blob, file.file.name || "image");
    } else if (typeof file === "string" && file.startsWith("data:")) {
        const fileBlob = await fetch(file).then((res) => res.blob());
        formData.append("file", fileBlob, "image");
    } else if (file instanceof File) {
        formData.append("file", file, file.name || "image");
    } else if (file instanceof Blob) {
        formData.append("file", file, "image");
    } else {
        console.error("Invalid file type:", Object.prototype.toString.call(file));
        console.error(file)
        throw new Error(`Invalid file type. Received type: ${Object.prototype.toString.call(file)}. Expected a File, Blob, base64 data URL string, or image URL.`);
    }

    const config = {
        headers: {
            "Content-Type": "multipart/form-data",
            "x-force-preflight": "true"
        },
        onUploadProgress: (progressEvent) => {
            if (onProgress && typeof onProgress === "function") {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onProgress(percentCompleted, progressEvent);
            }
        }
    };

    const response = await axiosInstance.post("/api/v2/upload/images", formData, config);

    if (response.hasOwnProperty("error")) {
        throw new Error(response.error);
    }
    return response.images[0].imageUrl;
};
